import {
  AlertTriangle,
  Ban,
  Clock,
  FileQuestion,
  Lock,
  ServerCrash,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';

// ───── Classification ─────
//
// Turns an unknown thrown value into a viewer-friendly description of *what kind*
// of error occurred and *what they can do about it* — without ever surfacing the
// raw message, payload, or stack trace (which can leak internal details).

export type ErrorKind =
  | 'offline'
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'badRequest'
  | 'conflict'
  | 'rateLimited'
  | 'server'
  | 'unknown';

export interface ClassifiedError {
  kind: ErrorKind;
  /** Short, human-friendly name for the nature of the error. */
  label: string;
  /** Actionable guidance for the user — contains no internal details. */
  guidance: string;
  /** HTTP status code when known. Safe to show; helps support triage. */
  code?: number;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function statusOf(e: Record<string, unknown>): number | undefined {
  const direct = e.statusCode ?? e.status;
  if (typeof direct === 'number') return direct;
  const response = asRecord(e.response);
  if (typeof response.status === 'number') return response.status;
  return undefined;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function fromStatus(status: number, thing: string): ClassifiedError {
  switch (true) {
    case status === 401:
      return { kind: 'unauthorized', code: status, label: 'Your session may have expired', guidance: 'Sign in again, then retry.' };
    case status === 403:
      return { kind: 'forbidden', code: status, label: "You don't have access", guidance: "You may not have permission to view this. If that's unexpected, contact your administrator." };
    case status === 404:
      return { kind: 'notFound', code: status, label: 'Not found', guidance: `${capitalize(thing)} may have been removed, or the link is out of date.` };
    case status === 408:
      return { kind: 'timeout', code: status, label: 'The request timed out', guidance: 'The server took too long to respond. Try again in a moment.' };
    case status === 409:
      return { kind: 'conflict', code: status, label: 'Update conflict', guidance: 'This changed since you loaded it. Reload to get the latest, then try again.' };
    case status === 429:
      return { kind: 'rateLimited', code: status, label: 'Too many requests', guidance: "You've hit a rate limit. Wait a moment, then try again." };
    case status >= 500:
      return { kind: 'server', code: status, label: 'Something went wrong on our end', guidance: "This is a problem on the server, not you. Try again shortly; if it keeps happening, contact support." };
    case status >= 400:
      return { kind: 'badRequest', code: status, label: "The request wasn't valid", guidance: 'Double-check the link, then reload and try again.' };
    default:
      return { kind: 'unknown', code: status, label: 'Unexpected response', guidance: 'Try reloading. If it keeps happening, contact support.' };
  }
}

function fromName(name: string, thing: string): ClassifiedError | null {
  switch (name) {
    case 'ResourceNotFoundError':
      return fromStatus(404, thing);
    case 'InvalidCredentialsError':
      return fromStatus(401, thing);
    case 'NotAuthorizedError':
      return fromStatus(403, thing);
    case 'BadRequestError':
      return fromStatus(400, thing);
    case 'InvalidArgumentError':
    case 'MissingParameterError':
      return fromStatus(409, thing);
    default:
      return null;
  }
}

export function classifyError(error: unknown, subject?: string): ClassifiedError {
  const e = asRecord(error);
  const thing = subject?.trim() || 'this item';
  const status = statusOf(e);
  const code = typeof e.code === 'string' ? e.code : '';
  const name = typeof e.name === 'string' ? e.name : '';
  const message = typeof e.message === 'string' ? e.message : '';

  // Offline first — the clearest, most fixable case.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { kind: 'offline', label: "You appear to be offline", guidance: 'Check your internet connection, then try again.' };
  }

  // A real HTTP status is the most reliable signal of what went wrong.
  if (typeof status === 'number') return fromStatus(status, thing);

  // No HTTP response came back — transport-level failure.
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(message)) {
    return { kind: 'timeout', label: 'The request timed out', guidance: 'The server took too long to respond. Try again in a moment.' };
  }
  if (code === 'ERR_NETWORK' || e.isAxiosError === true) {
    return { kind: 'network', label: "Couldn't reach the server", guidance: "We couldn't connect. Check your connection, then try again." };
  }

  // Known error classes that carry their meaning in the name.
  const byName = fromName(name, thing);
  if (byName) return byName;

  // Last resort — surface the error *type* (never the message) for context.
  return {
    kind: 'unknown',
    label: name && name !== 'Error' ? `Unexpected error (${name})` : 'Unexpected error',
    guidance: 'Try reloading. If it keeps happening, contact support.',
  };
}

// ───── Presentation ─────

const ICONS: Record<ErrorKind, LucideIcon> = {
  offline: WifiOff,
  network: WifiOff,
  timeout: Clock,
  unauthorized: Lock,
  forbidden: Ban,
  notFound: FileQuestion,
  badRequest: AlertTriangle,
  conflict: AlertTriangle,
  rateLimited: Clock,
  server: ServerCrash,
  unknown: AlertTriangle,
};

export interface ErrorInfoProps {
  /** The thrown value to describe. */
  error: unknown;
  /** What was being loaded, e.g. "this Stash Card". Used in the headline and 404 guidance. */
  subject?: string;
  className?: string;
}

/**
 * Reusable error panel. Explains the *nature* of a failure and how to resolve it,
 * deliberately omitting raw messages, payloads, and stack traces.
 */
export function ErrorInfo({ error, subject, className }: ErrorInfoProps): React.JSX.Element {
  const info = classifyError(error, subject);
  const Icon = ICONS[info.kind];
  const typeLine = typeof info.code === 'number' ? `${info.label} · ${info.code}` : info.label;

  return (
    <div
      role="alert"
      className={cn(
        'rounded-3xl border border-destructive/40 bg-destructive/5 p-10 text-center',
        className,
      )}
    >
      <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <Icon size={20} aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold text-destructive">
        {subject ? `Couldn't load ${subject}.` : 'Something went wrong.'}
      </p>
      <p className="mt-1 text-xs font-medium uppercase tracking-wide text-destructive/80">
        {typeLine}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{info.guidance}</p>
    </div>
  );
}
